import React, { useState, forwardRef, useEffect } from "react";
import Youtube from "react-youtube";
import playButton from "../../icons/play_button.png";
import IStyledProps from "../../utilities/IStyledProps";
import styled from "styled-components";
import classNames from "classnames";
import { useIsMobile } from "../../context/MobileContext";
import Picture from "../Picture";
import { getImagePath } from "../../../src/utilities/path";

const ThumbnailContainer = styled.div`
    position: relative;
    cursor: pointer;

    picture::after {
        display: block;
        content: "";
        padding-bottom: 56.25%;
    }

    .play-button {
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        &::after {
            padding-bottom: 0;
        }
    }

    img:not(.play-button) {
        object-fit: cover;
    }
`;

const YoutubeWrapper = styled.div<{isBuildr: boolean}>`
    ${(props) => props.isBuildr ? 'pointer-events: none;' : ''}
`;

const PlayerThumbnail = forwardRef<unknown, PlayerThumbnail>((props, ref) => {
    function handleClick() {
        if (typeof props.onClick === "function") {
            props.onClick();
        }
    }

    return (
        <ThumbnailContainer data-id={props.dataId} onClick={handleClick} className={classNames('yt-thumbnail-container', props.className)}>
            <Picture loading="lazy" src={getImagePath(props.src)} alt={props.alt} className="position-absolute left-0 top-0 w-100 h-100" />
            <Picture loading="lazy" src={playButton} alt="play" pictureClassName="play-button position-absolute" />
        </ThumbnailContainer>
    );
})

interface PlayerThumbnail {
    onClick: () => void;
    src: string;
    alt: string;
    dataId:string;
    className: string;
}

interface IYouTubeProps extends IStyledProps {
    videoId: string;
    src?: string;
    alt?: string;
    showThumbnail?: boolean;
    mute?: boolean;
    autoPlay?: boolean;
    subtitles?: boolean;
    cmsId?: string;
    editorClassObj?: any;
}

export default forwardRef(({ videoId, ...props }: IYouTubeProps, ref: any) => {
    const { src, alt, className = "", showThumbnail, mute, autoPlay, subtitles, editorClassObj, cmsId } = props;
    const thumbnailSrc = src ? `${process.env.GATSBY_MEDIA_URL || ""}${src}` : `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    const shouldPlay = showThumbnail || autoPlay;
    const { isMobileDevice } = useIsMobile();
    const [showPlayer, setShowPlayer] = useState(!showThumbnail);

    function handlePlayClick() {
        if (!cmsId) {
            setShowPlayer(true);
        }
    }

    useEffect(() => {
        setShowPlayer(!showThumbnail || isMobileDevice);
    }, [showThumbnail, isMobileDevice])

    return (
        <>
            {showPlayer ?
                <div data-id={cmsId} ref={ref} className={classNames('embed-responsive embed-responsive-16by9 yt-video-container', className, editorClassObj)}>
                    <YoutubeWrapper isBuildr={!!cmsId}>
                        <Youtube
                            className="embed-responsive-item"
                            videoId={videoId}
                            opts={{
                                playerVars: {
                                    autoplay: shouldPlay ? 1 : 0,
                                    cc_load_policy: subtitles ? 1 : null,
                                    mute: mute ? 1 : 0
                                }
                            }}
                        />
                    </YoutubeWrapper>
                </div>
                :
                <PlayerThumbnail
                    src={thumbnailSrc}
                    alt={alt}
                    onClick={handlePlayClick}
                    dataId={cmsId}
                    className={classNames(className, editorClassObj)}
                />
            }
        </>
    );
});
